import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import ModalPortal from "../sections/common/ModalPortal";
import PrescriptionVerificationService from "../service/PrescriptionVerificationService";
import "../styles/verifyPrescription.scss";

/**
 * "Verify prescription" — the header chip (Figma 3015:4405, "CTA · View all
 * documents" styled, beside the therapy tag) and the dialog it opens (Figma
 * 4471:4105, "Modal · Verify patient").
 *
 * THE FLOW, APIS AND RULES ARE LEGACY'S — bridge's new-bridge
 * PatientTrackingStatus.js ("Verify Patient"); only the UI is the board's:
 *   · the record is read on mount (GET /patients/{id}/verify). With no record
 *     there is nothing to verify, and legacy's button is inert — here the chip
 *     is not drawn at all (Nittin, 2026-09-17: remove it wherever it does not
 *     apply, never show it disabled). The same goes while the read is out and
 *     when it fails: the chip appears only once there is a record to answer;
 *   · three answers: Rx uploaded WITH the client drug, WITHOUT it (status 1 / 2,
 *     each asking whether the doctor matches the code: comment 1 / 2), or a
 *     wrong document (status 3, comment 3);
 *   · the saved answer is pre-selected (legacy's getVerifyStatus, which also
 *     reads an old "status 2, comment 0" as a wrong document);
 *   · the client-drug wording follows the patient's client code, verbatim
 *     (Pfizer's Folvite / Prevenar, Zydus, Daglif / Zyquis …);
 *   · Submit sends { status, commentType } (PUT) and the answer the server
 *     returns becomes the new saved one.
 * Deliberate differences, all about not losing an answer silently: choosing a
 * sub-option also chooses its group (legacy let a hidden comment change), Submit
 * waits for a complete answer, and a failed save keeps the dialog open with the
 * server's message instead of closing it.
 *
 * WHO SEES IT is the host's decision (phr: nutritionists only; bridge: every
 * user) — this component renders for whoever mounts it.
 *
 * THE DOCTOR CODE can be changed from the doctor card where the host passes
 * `doctorCode` — legacy bridge's "Doctor Information" dialog (new-bridge
 * DoctorInformation.js). bridge passes it (2026-10-13); phr does not, and there
 * the card stays read-only. Legacy's two calls: the typed code is looked up, the
 * card previews the doctor it resolves to, and Save makes that doctor the
 * patient's referrer. What follows from that (care team, the account the
 * patient lands on) is the server's, as it was for legacy. One deliberate
 * difference: Save is offered only for a code the lookup has just resolved.
 * Legacy showed Save before any lookup, and the server does not refuse an
 * unknown or empty code — it acts on it before failing
 * (api-user-service UpdateReferrerOfPatientId).
 *
 * This file is identical in phr-frontend and bridge-frontend.
 */

const PFIZER_CODES = ["03XXNA", "03XXND"];

/** Legacy's per-client wording for "the client drug" (PatientTrackingStatus.js). */
const drugLabel = (patient, withDrug) => {
  const p = patient || {};
  const code = p.clientCode;
  const phone = String(p.phoneno == null ? "" : p.phoneno);
  const w = withDrug ? "with" : "without";
  const pfizer = PFIZER_CODES.indexOf(code) !== -1;
  if (code === "03TORR") return `Prescription (Rx) uploaded ${w} client drug`;
  if (pfizer && code === "03XXNA")
    return `Prescription (Rx) uploaded ${w} Folvite/ Folvite Active/ Folvite DHA/ Folvite MB/ Autrin/ Autrin XT`;
  if (pfizer && code === "03XXND" && phone.startsWith("33")) return `Prescription (Rx) uploaded ${w} Prevenar`;
  if (pfizer && code === "03XXND")
    return `Prescription (Rx) uploaded ${w} Folvite/ Folvite Active/ Folvite DHA/ Folvite MB/ Autrin/ Autrin XT`;
  if (code === "03XXLS") return `Prescription (Rx) uploaded ${w} Zydus drug`;
  if (code === "03ZYSA") return `Prescription (Rx) uploaded ${w} Daglif/ Zyquis`;
  return `Prescription (Rx) uploaded ${w} client drug`;
};

/** Legacy's button text for a saved record — the chip's LABEL, as legacy's is
    (getButtonText, PatientTrackingStatus.js). Two deliberate wordings: legacy
    says "Active" for a verified-on-drug record, which reads as a patient
    status next to the other chips, and it capitalises "Verification". */
export const savedStateText = (record, patient) => {
  if (!record) return "";
  const status = Number(record.status);
  const comment = Number(record.comment);
  const p = patient || {};
  const code = p.clientCode;
  const phone = String(p.phoneno == null ? "" : p.phoneno);
  if (status === 0) return "Not verified yet";
  if (status === 1) return comment === 1 ? "Verified" : "Unsuccessful verification";
  if (status === 2) {
    if (comment !== 1) return "Unsuccessful verification";
    if (code === "03XXND" && phone.startsWith("33")) return "Rx verified, not on Prevenar";
    if (code === "03XXNA" || code === "03XXND") return "Rx verified, not on Folvite…";
    if (code === "03XXLS") return "Rx verified, not on Zydus…";
    if (code === "03ZYSA") return "Rx verified, not on Daglif/ Zyquis";
    return "Rx verified, not on client drug";
  }
  if (status === 3) return "Unsuccessful verification";
  return "";
};

/** The saved record as a selection: { status, comment } or nothing chosen. */
export const selectionOf = (record) => {
  if (!record) return { status: 0, comment: 0 };
  const status = Number(record.status);
  const comment = Number(record.comment);
  if (status === 1 || (status === 2 && comment !== 0)) {
    return { status, comment: comment === 1 || comment === 2 ? comment : 0 };
  }
  /* Legacy reads "status 2, comment 0" as the wrong-document answer. */
  if (status === 3 || (status === 2 && comment === 0)) return { status: 3, comment: 3 };
  return { status: 0, comment: 0 };
};

const initialsOf = (name) =>
  String(name || "")
    .replace(/^dr\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

const errorText = (err, fallback) => {
  if (typeof err === "string" && err.trim()) return err.trim();
  if (err && typeof err.message === "string" && err.message.trim()) return err.message.trim();
  return fallback;
};

/** A doctor code as it is looked up and saved: trimmed and upper-cased, since
    the server upper-cases the code it stores and the lookup must check the
    same string. */
const normalCode = (s) => String(s == null ? "" : s).trim().toUpperCase();

/* The pencil beside the patient's name in bridge's identity band (Figma
   5125:4527), path verbatim; coloured by `currentColor`. */
const PencilIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M3.3332 12.6665H4.1742L10.9985 5.8422L10.1575 5.0012L3.3332 11.8255V12.6665ZM2.3332 13.6665V11.4102L11.1269 2.62037C11.2276 2.52881 11.3389 2.45809 11.4607 2.4082C11.5826 2.3582 11.7104 2.3332 11.844 2.3332C11.9777 2.3332 12.1071 2.35693 12.2324 2.40437C12.3577 2.45181 12.4686 2.52726 12.5652 2.6307L13.3794 3.45504C13.4828 3.55159 13.5565 3.6627 13.6005 3.78837C13.6445 3.91404 13.6665 4.0397 13.6665 4.16537C13.6665 4.29948 13.6436 4.42743 13.5979 4.5492C13.5521 4.67109 13.4793 4.78243 13.3794 4.8832L4.58954 13.6665H2.3332ZM10.5707 5.42904L10.1575 5.0012L10.9985 5.8422L10.5707 5.42904Z" />
  </svg>
);

const Radio = ({ on, size }) => (
  <span className={`vrx-radio${on ? " is-on" : ""}${size === "sm" ? " is-sm" : ""}`} aria-hidden="true">
    {on ? <span className="vrx-radio-dot" /> : null}
  </span>
);
Radio.propTypes = { on: PropTypes.bool, size: PropTypes.oneOf(["sm", "md"]) };

const SUB_OPTIONS = [
  { comment: 1, label: "Have same doctor as per code added" },
  { comment: 2, label: "Doctor is not as per code" },
];

const VerifyDialog = ({ patient, record, doctorCode, onClose, onSaved, onDoctorSaved }) => {
  const pid = patient && (patient.id || patient.patientID);
  const [pick, setPick] = useState(() => selectionOf(record));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  /* The photo that failed to load, by URL: the card can switch doctors. */
  const [brokenSrc, setBrokenSrc] = useState("");
  /* The doctor-code editor (only with `doctorCode`): the code being typed, the
     doctor the lookup resolved it to, and the lookup / save in flight. */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [found, setFound] = useState(null);
  const [finding, setFinding] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [codeSaving, setCodeSaving] = useState(false);
  const service = useRef(new PrescriptionVerificationService());
  const alive = useRef(true);
  const cardRef = useRef(null);
  const inputRef = useRef(null);
  const pencilRef = useRef(null);
  const refocusPencil = useRef(false);
  /* Bumped by every edit of the code, so a lookup answering for a code that
     has since changed is dropped. */
  const lookupSeq = useRef(0);
  useEffect(() => () => { alive.current = false; }, []);

  const busy = saving || codeSaving;

  const stopEdit = useCallback(() => {
    lookupSeq.current += 1;
    refocusPencil.current = true;
    setEditing(false);
    setDraft("");
    setFound(null);
    setFinding(false);
    setCodeError("");
  }, []);

  /* Escape leaves the code editor first, and the dialog only after that. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || busy) return;
      if (editing) stopEdit();
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy, editing, stopEdit]);

  useEffect(() => {
    if (cardRef.current) cardRef.current.focus();
  }, []);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else if (!editing && refocusPencil.current) {
      refocusPencil.current = false;
      if (pencilRef.current) pencilRef.current.focus();
    }
  }, [editing]);

  const doctor = (record && record.doctor) || {};
  const hasDoctor = !!(doctor && (doctor.id || doctor.name));
  /* A resolved code that is already the patient's changes nothing (the server
     returns without touching the account), so it is not offered for saving. */
  const same =
    !!found && (normalCode(draft) === normalCode(doctor.code) || (!!doctor.id && String(found.id) === String(doctor.id)));
  const canSave = editing && !!found && !same && !finding && !codeSaving;
  /* While a resolved code waits to be saved, the card previews its doctor. */
  const preview = editing && !!found && !same;
  const shown = preview ? found : doctor;
  const qualifications = [shown.title, shown.speciality].map((x) => String(x || "").trim()).filter(Boolean).join(" — ");
  const complete = pick.status === 3 || ((pick.status === 1 || pick.status === 2) && (pick.comment === 1 || pick.comment === 2));

  const startEdit = () => {
    if (busy) return;
    lookupSeq.current += 1;
    setDraft(doctor.code || "");
    setFound(null);
    setFinding(false);
    setCodeError("");
    setEditing(true);
  };

  const typeCode = (value) => {
    lookupSeq.current += 1;
    setDraft(value);
    setFound(null);
    setFinding(false);
    setCodeError("");
  };

  const findDoctor = () => {
    const code = normalCode(draft);
    if (!code || finding || codeSaving) return;
    const mine = ++lookupSeq.current;
    const notFound = `No doctor found with code ${code}.`;
    setFinding(true);
    setFound(null);
    setCodeError("");
    doctorCode.lookup(
      code,
      (res) => {
        if (!alive.current || mine !== lookupSeq.current) return;
        setFinding(false);
        if (res && res.id) setFound(res);
        else setCodeError(notFound);
      },
      (err) => {
        if (!alive.current || mine !== lookupSeq.current) return;
        setFinding(false);
        const msg = errorText(err, "");
        setCodeError(!msg || /not found/i.test(msg) ? notFound : msg);
      }
    );
  };

  const codeSaved = (fresh, chosen) => {
    if (!alive.current) return;
    const next = fresh || { ...record, doctor: chosen };
    setCodeSaving(false);
    stopEdit();
    if (Number(next.status) !== Number(record.status) || Number(next.comment) !== Number(record.comment)) {
      setPick(selectionOf(next));
    }
    EventService.emit("showError", { message: "Doctor code updated", tone: "success" });
    if (onDoctorSaved) onDoctorSaved(next);
  };

  const saveCode = () => {
    if (!canSave) return;
    const chosen = found;
    setCodeSaving(true);
    setCodeError("");
    doctorCode.save(
      pid,
      normalCode(draft),
      () => {
        if (!alive.current) return;
        /* Re-read rather than trust the reply: it is the doctor or the whole
           patient depending on the server's branch, while the record's doctor
           is the patient's referrer as this GET reads it. */
        service.current.get(
          pid,
          (res) => codeSaved(res && res.status != null ? res : null, chosen),
          () => codeSaved(null, chosen)
        );
      },
      (err) => {
        if (!alive.current) return;
        setCodeSaving(false);
        setCodeError(errorText(err, "Couldn't change the doctor code. Please try again."));
      }
    );
  };

  const choose = (status, comment) => {
    setError("");
    if (status === 3) return setPick({ status: 3, comment: 3 });
    /* Picking a group keeps the doctor answer already given (legacy
       `comment || "1"`), defaulting to "same doctor". */
    setPick((cur) => ({
      status,
      comment: comment || (cur.comment === 1 || cur.comment === 2 ? cur.comment : 1),
    }));
    return undefined;
  };

  const submit = () => {
    if (!complete || busy || editing) return;
    setSaving(true);
    setError("");
    service.current.update(
      pid,
      pick.status,
      pick.comment,
      (res) => {
        if (!alive.current) return;
        setSaving(false);
        const saved = {
          ...record,
          status: res && res.status != null ? res.status : pick.status,
          comment: res && res.comment != null ? res.comment : pick.comment,
        };
        EventService.emit("showError", { message: "Prescription verification saved", tone: "success" });
        onSaved(saved);
      },
      (err) => {
        if (!alive.current) return;
        setSaving(false);
        setError(errorText(err, "Couldn't save the verification. Please try again."));
      }
    );
  };

  const group = (status) => {
    const on = pick.status === status;
    return (
      <div className={`vrx-group${on ? " is-on" : ""}`} key={status} role="group" aria-label={drugLabel(patient, status === 1)}>
        <button
          type="button"
          role="radio"
          aria-checked={on}
          className="vrx-row"
          disabled={busy}
          onClick={() => choose(status)}
        >
          <Radio on={on} />
          <span className="vrx-row-label">{drugLabel(patient, status === 1)}</span>
        </button>
        <div className="vrx-subs" role="radiogroup" aria-label="Does the doctor match the code?">
          {SUB_OPTIONS.map((o) => {
            const subOn = on && pick.comment === o.comment;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={subOn}
                key={o.comment}
                className={`vrx-sub${subOn ? " is-on" : ""}`}
                disabled={busy}
                onClick={() => choose(status, o.comment)}
              >
                <Radio on={subOn} size="sm" />
                <span className="vrx-sub-label">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const codeNote = codeError
    ? { text: codeError, error: true }
    : finding
    ? { text: "Looking up…" }
    : same
    ? { text: "This is already the patient's doctor code." }
    : preview
    ? { text: `Save to make ${found.name || "this doctor"} the patient's doctor.` }
    : null;

  const codeEditor = (
    <div className="vrx-code-edit">
      <div className="vrx-code-row">
        <label className="vrx-code-label" htmlFor="vrx-code-input">
          Doctor code:
        </label>
        <input
          ref={inputRef}
          id="vrx-code-input"
          className="vrx-code-input"
          value={draft}
          maxLength={32}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={codeError ? "true" : "false"}
          aria-describedby={codeNote ? "vrx-code-note" : undefined}
          disabled={codeSaving}
          onChange={(e) => typeCode(e.target.value)}
          onKeyDown={(e) => {
            /* Enter looks the code up. Saving moves the patient's account, so
               it is never one keystroke away from the lookup. */
            if (e.key === "Enter") {
              e.preventDefault();
              findDoctor();
            }
          }}
        />
        <button
          type="button"
          className="vrx-code-btn is-ghost"
          disabled={!normalCode(draft) || finding || codeSaving}
          onClick={findDoctor}
        >
          Find
        </button>
      </div>
      {codeNote ? (
        <p
          id="vrx-code-note"
          className={`vrx-code-note${codeNote.error ? " is-error" : ""}`}
          role={codeNote.error ? "alert" : undefined}
        >
          {codeNote.text}
        </p>
      ) : null}
      <div className="vrx-code-actions">
        <button type="button" className="vrx-code-btn" disabled={codeSaving} onClick={stopEdit}>
          Cancel
        </button>
        <button type="button" className="vrx-code-btn is-primary" disabled={!canSave} onClick={saveCode}>
          {codeSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <ModalPortal>
      <div
        className="vrx-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !busy) onClose();
        }}
      >
        <div
          ref={cardRef}
          className="vrx-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vrx-title"
          tabIndex={-1}
        >
          <div className="vrx-head">
            <div className="vrx-title-row">
              <h2 className="vrx-title" id="vrx-title">
                Verify prescription
              </h2>
              <button type="button" className="vrx-x" aria-label="Close" disabled={busy} onClick={onClose}>
                <svg viewBox="0 0 28 28" width="28" height="28" fill="none" aria-hidden="true">
                  <path d="M8.5 8.5L19.5 19.5M19.5 8.5L8.5 19.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            {hasDoctor ? (
              <div className={`vrx-doctor${preview ? " is-preview" : ""}`} aria-label="Referring doctor">
                {shown.profile_image && brokenSrc !== shown.profile_image ? (
                  <img
                    className="vrx-photo"
                    src={shown.profile_image}
                    alt=""
                    onError={() => setBrokenSrc(shown.profile_image)}
                  />
                ) : (
                  <span className="vrx-photo is-initials" aria-hidden="true">
                    {initialsOf(shown.name) || "Dr"}
                  </span>
                )}
                <div className="vrx-doctor-text">
                  <div className="vrx-doctor-meta">
                    <p className="vrx-doctor-name">{shown.name || "Referring doctor"}</p>
                    {qualifications ? <p className="vrx-doctor-quals">{qualifications}</p> : null}
                  </div>
                  {editing ? (
                    codeEditor
                  ) : doctor.code || doctorCode ? (
                    <p className={`vrx-doctor-code${doctorCode ? " has-edit" : ""}`}>
                      Doctor code: {doctor.code || "—"}
                      {doctorCode ? (
                        <button
                          ref={pencilRef}
                          type="button"
                          className="vrx-code-pencil"
                          aria-label="Edit doctor code"
                          title="Edit doctor code"
                          disabled={busy}
                          onClick={startEdit}
                        >
                          <PencilIcon />
                        </button>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="vrx-body">
            <p className="vrx-question">What does the uploaded document show?</p>
            {error ? (
              <p className="vrx-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="vrx-options" role="radiogroup" aria-label="What does the uploaded document show?">
              {group(1)}
              <span className="vrx-divider" aria-hidden="true" />
              {group(2)}
              <span className="vrx-divider" aria-hidden="true" />
              <div className={`vrx-group is-last${pick.status === 3 ? " is-on" : ""}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={pick.status === 3}
                  className="vrx-row"
                  disabled={busy}
                  onClick={() => choose(3)}
                >
                  <Radio on={pick.status === 3} />
                  <span className="vrx-row-label">Wrong document</span>
                </button>
              </div>
            </div>
          </div>

          {/* Submit waits while the doctor code is open: the answers ask
              whether the doctor matches the code, and closing on Submit would
              drop the half-made edit without a word. */}
          <div className="vrx-foot">
            <button type="button" className="vrx-btn" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="vrx-btn is-primary"
              disabled={!complete || busy || editing}
              onClick={submit}
            >
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

const doctorCodeShape = PropTypes.shape({
  lookup: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
});

VerifyDialog.propTypes = {
  patient: PropTypes.object,
  record: PropTypes.object,
  doctorCode: doctorCodeShape,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  /** With the re-read record, after a doctor code is saved. */
  onDoctorSaved: PropTypes.func,
};

/** The header chip, and the dialog behind it. */
const VerifyPrescription = ({ patient, doctorCode, onDoctorCodeSaved }) => {
  const pid = patient && (patient.id || patient.patientID);
  /* The saved record, once read — null while loading, with none, or on a failed read. */
  const [record, setRecord] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!pid) return undefined;
    let alive = true;
    setRecord(null);
    new PrescriptionVerificationService().get(
      pid,
      (res) => {
        if (alive) setRecord(res && res.status != null ? res : null);
      },
      () => {
        if (alive) setRecord(null);
      }
    );
    return () => {
      alive = false;
    };
  }, [pid]);

  useEffect(() => load(), [load]);

  if (!pid || !record) return null;

  return (
    <>
      {/* The LABEL is the saved state once there is one, as legacy's button is:
          the answer submitted in the dialog is what the header then reads. Only
          a pending record (status 0, nothing submitted yet) keeps the call to
          action. The full text stays in the tooltip, since a long state
          ellipsizes (see verifyPrescription.scss). */}
      <button
        type="button"
        className="ph-chip vrx-chip"
        title={`Verify prescription — current: ${savedStateText(record, patient)}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {Number(record.status) === 0 ? "Verify prescription" : savedStateText(record, patient)}
      </button>
      {open ? (
        <VerifyDialog
          patient={patient}
          record={record}
          doctorCode={doctorCode}
          onClose={() => setOpen(false)}
          onSaved={(saved) => {
            setOpen(false);
            setRecord(saved);
          }}
          onDoctorSaved={(next) => {
            /* In place, never through load(): that empties the record first,
               which would unmount this dialog under the user. */
            setRecord(next);
            if (onDoctorCodeSaved) onDoctorCodeSaved();
          }}
        />
      ) : null}
    </>
  );
};

VerifyPrescription.propTypes = {
  patient: PropTypes.object,
  /**
   * Given, the dialog's doctor card can change the patient's doctor code (see
   * the note at the top). bridge passes its DoctorCodeService; phr passes
   * nothing. Both methods end in (onResponse, onError):
   *   lookup(code, …)            GET /docprofile/doctor?code={code}
   *   save(patientId, code, …)   PUT /patients/{id}/referrer { doctorCode }
   */
  doctorCode: doctorCodeShape,
  /**
   * After a doctor code is saved. The server may have moved the patient to
   * another account, and the answers' wording follows the patient's client
   * code, so the host re-reads the patient.
   */
  onDoctorCodeSaved: PropTypes.func,
};

export default VerifyPrescription;
